const prefetchedPages = new Set();
const MAX_PREFETCHED_PAGES = 10;
const HOVER_INTENT_DELAY = 80;
let hoverTimer;

/**
 * Resolve a safe, cacheable storefront destination from an interaction target.
 * Cart, checkout, account, and utility routes are intentionally excluded so
 * prefetched HTML cannot become stale or expose session-specific content.
 *
 * @param {EventTarget | null} target
 * @returns {{ anchor: HTMLAnchorElement, url: URL } | null}
 */
function getPrefetchTarget(target) {
  if (!(target instanceof Element)) return null;

  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.target && anchor.target !== '_self') return null;
  if (anchor.hasAttribute('download') || anchor.hasAttribute('data-no-prefetch')) return null;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  if (!['http:', 'https:'].includes(url.protocol)) return null;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return null;
  if (/^\/(cart|checkout|checkouts|account|admin|apps)(\/|$)/.test(url.pathname)) return null;

  url.hash = '';
  return { anchor, url };
}

/**
 * Warm the next HTML document after a visitor signals intent to open it.
 *
 * @param {EventTarget | null} target
 */
function prefetchPage(target) {
  const destination = getPrefetchTarget(target);
  if (!destination || prefetchedPages.size >= MAX_PREFETCHED_PAGES) return;

  const href = destination.url.href;
  if (prefetchedPages.has(href)) return;
  prefetchedPages.add(href);

  const prefetch = document.createElement('link');
  prefetch.rel = 'prefetch';
  prefetch.as = 'document';
  prefetch.href = href;
  prefetch.fetchPriority = 'low';
  document.head.append(prefetch);
}

document.addEventListener(
  'pointerover',
  (event) => {
    const destination = getPrefetchTarget(event.target);
    if (
      !destination ||
      (event.relatedTarget instanceof Node && destination.anchor.contains(event.relatedTarget))
    ) {
      return;
    }

    window.clearTimeout(hoverTimer);
    hoverTimer = window.setTimeout(() => prefetchPage(destination.anchor), HOVER_INTENT_DELAY);
  },
  { passive: true }
);

document.addEventListener(
  'pointerout',
  (event) => {
    const destination = getPrefetchTarget(event.target);
    if (
      !destination ||
      (event.relatedTarget instanceof Node && destination.anchor.contains(event.relatedTarget))
    ) {
      return;
    }
    window.clearTimeout(hoverTimer);
  },
  { passive: true }
);

document.addEventListener('focusin', (event) => prefetchPage(event.target));
document.addEventListener('touchstart', (event) => prefetchPage(event.target), { passive: true });
